import { collection, getDocs, query, where, orderBy, limit as firestoreLimit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { subnetNames } from '../../utils/subnetNames';
import { subnetDescriptions } from '../../utils/subnetDescriptions';
import { generateMarketAnalysis, generateMarketMovementAnalysis } from '../openaiService';
import { rateLimiter } from '../rateLimiter';
import { formatDistanceToNow } from 'date-fns';
import axios from 'axios';
import type { SubnetPool } from '../../types/subnet';
import { getRecentTweets } from './utils';
import { getMindshareHistory } from '../mindshareService';

interface VolumeData {
  volume_24h: number;
  buy_volume_24h: number;
  sell_volume_24h: number;
  buys_24h: number;
  sells_24h: number;
  timestamp: string;
}

interface PotentialSubnet {
  netuid: number;
  name: string;
  score: number;
  reasons: string[];
  priceChange24h: number;
  volumeChange24h: number;
  holderChange24h: number;
  recentTweets: string[];
  buyPressure: number;
  twitterSentiment: 'bullish' | 'neutral' | 'bearish';
  buildingProgress: string;
}

// Get latest volume data from Firestore
async function getLatestVolumeData(netuid: number): Promise<VolumeData | null> {
  try {
    const volumeRef = collection(db, 'subnet_volume', netuid.toString(), 'entries');
    const q = query(
      volumeRef,
      orderBy('timestamp', 'desc'),
      firestoreLimit(1)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    return snapshot.docs[0].data() as VolumeData;
  } catch (error) {
    console.error('Error fetching volume data:', error);
    return null;
  }
}

// Analyze Twitter sentiment for a subnet
async function analyzeTwitterSentiment(tweets: any[]): Promise<{
  sentiment: 'bullish' | 'neutral' | 'bearish';
  buildingProgress: string;
}> {
  if (tweets.length === 0) {
    return { sentiment: 'neutral', buildingProgress: '' };
  }
  
  // Look for bullish keywords in tweets
  const bullishKeywords = [
    'launch', 'released', 'milestone', 'partnership', 'integration',
    'update', 'upgrade', 'improvement', 'growth', 'progress',
    'success', 'achievement', 'breakthrough', 'innovation', 'development',
    'beta', 'alpha', 'testnet', 'mainnet', 'release', 'version',
    'feature', 'enhancement', 'collaboration', 'excited', 'proud',
    'happy to announce', 'thrilled', 'introducing', 'new'
  ];
  
  // Look for bearish keywords in tweets
  const bearishKeywords = [
    'delay', 'postpone', 'issue', 'problem', 'bug', 'error',
    'vulnerability', 'security', 'fix', 'patch', 'concern',
    'challenge', 'difficult', 'sorry', 'apologize', 'unfortunately'
  ];
  
  let bullishCount = 0;
  let bearishCount = 0;
  let buildingDetails = '';
  
  // Analyze recent tweets (last 7 days)
  const recentTweets = tweets.filter(tweet => {
    const tweetDate = new Date(tweet.created_at);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return tweetDate >= sevenDaysAgo;
  });
  
  if (recentTweets.length === 0) {
    return { sentiment: 'neutral', buildingProgress: '' };
  }
  
  // Find the most informative tweet about what they're building
  let mostInformativeTweet = '';
  let highestInformationScore = 0;
  
  recentTweets.forEach(tweet => {
    const tweetText = tweet.text.toLowerCase();
    
    // Count bullish and bearish keywords
    bullishKeywords.forEach(keyword => {
      if (tweetText.includes(keyword.toLowerCase())) {
        bullishCount++;
      }
    });
    
    bearishKeywords.forEach(keyword => {
      if (tweetText.includes(keyword.toLowerCase())) {
        bearishCount++;
      }
    });
    
    // Calculate information score for this tweet
    const informationScore = 
      (tweet.text.length / 280) * 0.3 + // Longer tweets might have more info
      (tweet.like_count / 100) * 0.3 + // More likes might indicate importance
      (tweet.retweet_count / 50) * 0.4; // More retweets might indicate importance
    
    // Check if this tweet contains development-related terms
    const developmentTerms = ['build', 'develop', 'launch', 'release', 'feature', 'update', 'progress'];
    const hasDevelopmentTerms = developmentTerms.some(term => tweetText.includes(term));
    
    if (hasDevelopmentTerms && informationScore > highestInformationScore) {
      highestInformationScore = informationScore;
      mostInformativeTweet = tweet.text;
    }
  });
  
  // Determine sentiment based on keyword counts
  let sentiment: 'bullish' | 'neutral' | 'bearish' = 'neutral';
  
  if (bullishCount > bearishCount * 2) {
    sentiment = 'bullish';
  } else if (bearishCount > bullishCount) {
    sentiment = 'bearish';
  }
  
  // Extract building progress from most informative tweet
  buildingDetails = mostInformativeTweet;
  
  return { sentiment, buildingProgress: buildingDetails };
}

// Analyze subnet potential based on multiple factors
async function analyzeSubnetPotential(netuid: number): Promise<PotentialSubnet | null> {
  try {
    // Skip unknown subnets
    if (subnetNames[netuid].toLowerCase().includes('unknown')) {
      return null;
    }
    
    // Get latest pool data
    const poolSnapshot = await getDocs(query(
      collection(db, `subnet_pools/${netuid}/entries`),
      orderBy('timestamp', 'desc'),
      firestoreLimit(2)
    ));

    if (poolSnapshot.empty) return null;

    const entries = poolSnapshot.docs.map(doc => doc.data() as SubnetPool);
    const latestEntry = entries[0];
    const previousEntry = entries[1];

    // Get volume data
    const volumeData = await getLatestVolumeData(netuid);
    
    // Get recent tweets
    const tweets = await getRecentTweets(netuid);
    
    // Analyze Twitter sentiment
    const { sentiment: twitterSentiment, buildingProgress } = await analyzeTwitterSentiment(tweets);
    
    // Get holder data
    const holderSnapshot = await getDocs(query(
      collection(db, 'netuid_stats', netuid.toString(), 'entries'),
      orderBy('timestamp', 'desc'),
      firestoreLimit(2)
    ));

    const holderEntries = holderSnapshot.docs.map(doc => doc.data());
    const latestHolders = holderEntries[0]?.holders || 0;
    const previousHolders = holderEntries[1]?.holders || 0;

    // Calculate metrics
    const priceChange24h = Number(latestEntry.price_change_1_day);
    const volumeChange24h = volumeData ? 
      ((volumeData.volume_24h - volumeData.volume_24h) / volumeData.volume_24h) * 100 : 0;
    const holderChange24h = previousHolders > 0 ? 
      ((latestHolders - previousHolders) / previousHolders) * 100 : 0;
    const buyPressure = volumeData ? 
      volumeData.buys_24h / (volumeData.buys_24h + volumeData.sells_24h) : 0;

    // Score calculation
    let score = 0;
    const reasons: string[] = [];

    // Twitter sentiment analysis (heavily weighted)
    if (twitterSentiment === 'bullish') {
      score += 40;
      if (buildingProgress) {
        reasons.push(`Active development with recent updates on ${buildingProgress.substring(0, 50)}...`);
      } else {
        reasons.push('Strong positive sentiment on social media');
      }
    }

    // Price analysis
    if (priceChange24h >= 10) {
      score += 25;
      reasons.push(`Strong upward momentum with ${priceChange24h.toFixed(1)}% price increase`);
    } else if (priceChange24h >= 5) {
      score += 15;
      reasons.push(`Positive price action with ${priceChange24h.toFixed(1)}% gain`);
    } else if (priceChange24h <= -10 && buyPressure > 0.6) {
      // Price drop but high buy pressure indicates accumulation
      score += 20;
      reasons.push(`Potential accumulation zone after ${Math.abs(priceChange24h).toFixed(1)}% correction`);
    }

    // Volume analysis
    if (volumeChange24h > 50) {
      score += 20;
      reasons.push(`Significant volume increase of ${volumeChange24h.toFixed(1)}%`);
    } else if (volumeChange24h > 20) {
      score += 10;
      reasons.push(`Healthy volume growth of ${volumeChange24h.toFixed(1)}%`);
    }

    // Holder analysis
    if (holderChange24h > 10) {
      score += 25;
      reasons.push(`Strong holder growth of ${holderChange24h.toFixed(1)}%`);
    } else if (holderChange24h > 5) {
      score += 15;
      reasons.push(`Steady holder accumulation of ${holderChange24h.toFixed(1)}%`);
    }

    // Buy pressure analysis
    if (buyPressure > 0.65) {
      score += 25;
      reasons.push(`High buy pressure with ${(buyPressure * 100).toFixed(1)}% buy ratio`);
    } else if (buyPressure > 0.55) {
      score += 15;
      reasons.push(`Positive buy pressure with ${(buyPressure * 100).toFixed(1)}% buy ratio`);
    }

    // Recent development activity
    if (tweets.length > 0) {
      score += Math.min(tweets.length * 5, 20);
      
      // If we don't already have a reason about development from sentiment analysis
      if (!reasons.some(r => r.includes('development'))) {
        reasons.push(`Active development with ${tweets.length} recent updates`);
      }
    }

    // Add points for having a clear description of what they're building
    const subnetInfo = subnetDescriptions[netuid];
    if (subnetInfo && subnetInfo.description && subnetInfo.description.length > 100) {
      score += 10;
      
      // Extract what they're building from the description
      const buildingMatch = subnetInfo.description.match(/(?:building|developing|creating|focuses on|platform for|network for|system for|framework for) ([^.]+)/i);
      if (buildingMatch) {
        reasons.push(`Building ${buildingMatch[1]}`);
      }
    }

    // Only return subnets with significant potential
    if (score >= 40) {
      return {
        netuid,
        name: subnetNames[netuid],
        score,
        reasons,
        priceChange24h,
        volumeChange24h,
        holderChange24h,
        recentTweets: tweets.slice(0, 2).map(t => t.text),
        buyPressure,
        twitterSentiment,
        buildingProgress
      };
    }

    return null;
  } catch (error) {
    console.error(`Error analyzing subnet ${netuid}:`, error);
    return null;
  }
}

// Generate a summary of recent tweets
async function generateTweetSummary(netuid: number): Promise<string> {
  try {
    const tweets = await getRecentTweets(netuid);
    if (!tweets || tweets.length === 0) return '';

    // Sort tweets by date (newest first)
    const sortedTweets = tweets.sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    // Take the 5 most recent tweets
    const recentTweets = sortedTweets.slice(0, 5);

    // Format tweets for analysis
    const tweetTexts = recentTweets.map(tweet => {
      const date = formatDistanceToNow(new Date(tweet.created_at), { addSuffix: true });
      return `[${date}] ${tweet.text}`;
    }).join('\n');

    // Generate summary using OpenAI
    const summary = await generateMarketMovementAnalysis(tweetTexts);
    return summary ? `\n\nRecent Updates:\n${summary}` : '';
  } catch (error) {
    console.error('Error generating tweet summary:', error);
    return '';
  }
}

async function getPriceReport(netuid: number): Promise<string> {
  try {
    console.log(`Fetching price data for subnet ${netuid}`);
    
    await rateLimiter.checkRateLimit();
    const taoResponse = await axios.get(
      'https://api.taostats.io/api/price/latest/v1?asset=tao',
      {
        headers: {
          'accept': 'application/json',
          'Authorization': 'tao-1b237a3c-dcb4-468c-ad70-6b47e40e2552:6473c9a3'
        }
      }
    );
    
    const taoPrice = taoResponse.data?.data?.[0]?.price 
      ? Number(taoResponse.data.data[0].price) 
      : null;
    
    const [entriesSnapshot, volumeData] = await Promise.all([
      getDocs(query(
        collection(db, `subnet_pools/${netuid}/entries`),
        orderBy('timestamp', 'desc'),
        firestoreLimit(1)
      )),
      getLatestVolumeData(netuid)
    ]);
    
    if (!entriesSnapshot.empty) {
      const latestData = entriesSnapshot.docs[0].data() as SubnetPool;
      const subnetPrice = Number(latestData.price);
      const usdPrice = taoPrice ? subnetPrice * taoPrice : null;
      
      // Direct report without AI analysis
      let report = `Subnet ${netuid} (${subnetNames[netuid]})
Price: ${subnetPrice.toFixed(4)} τ${usdPrice ? ` ($${usdPrice.toFixed(4)})` : ''}
1h Change: ${Number(latestData.price_change_1_hour).toFixed(2)}%
24h Change: ${Number(latestData.price_change_1_day).toFixed(2)}%`;

      // Add volume data if available
      if (volumeData) {
        const volume24h = volumeData.volume_24h / 1e9;
        const buyVolume = volumeData.buy_volume_24h / 1e9;
        const sellVolume = volumeData.sell_volume_24h / 1e9;
        
        const volumeUsd = taoPrice ? volume24h * taoPrice : null;
        const buyVolumeUsd = taoPrice ? buyVolume * taoPrice : null;
        const sellVolumeUsd = taoPrice ? sellVolume * taoPrice : null;
        
        report += `\n\nVolume (24h): ${volume24h.toFixed(4)} τ${volumeUsd ? ` ($${volumeUsd.toFixed(2)})` : ''}
Buy Volume: ${buyVolume.toFixed(4)} τ${buyVolumeUsd ? ` ($${buyVolumeUsd.toFixed(2)})` : ''} (${volumeData.buys_24h} trades)
Sell Volume: ${sellVolume.toFixed(4)} τ${sellVolumeUsd ? ` ($${sellVolumeUsd.toFixed(2)})` : ''} (${volumeData.sells_24h} trades)`;
      }

      return report;
    }
    
    return `No price data available for Subnet ${netuid} (${subnetNames[netuid]}).`;
  } catch (error) {
    console.error('Error getting price:', error);
    if (error instanceof Error) {
      return `Error: Failed to get price data - ${error.message}`;
    }
    return 'Error: Failed to get price data.';
  }
}

async function getMarketCapReport(netuid: number, hours: number): Promise<string> {
  try {
    await rateLimiter.checkRateLimit();
    const taoResponse = await axios.get(
      'https://api.taostats.io/api/price/latest/v1?asset=tao',
      {
        headers: {
          'accept': 'application/json',
          'Authorization': 'tao-1b237a3c-dcb4-468c-ad70-6b47e40e2552:6473c9a3'
        }
      }
    );
    
    const taoPrice = taoResponse.data?.data?.[0]?.price 
      ? Number(taoResponse.data.data[0].price) 
      : null;

    const [latestSnapshot, volumeData] = await Promise.all([
      getDocs(query(
        collection(db, `subnet_pools/${netuid}/entries`),
        orderBy('timestamp', 'desc'),
        firestoreLimit(1)
      )),
      getLatestVolumeData(netuid)
    ]);
    
    if (latestSnapshot.empty) {
      return `No market data available for Subnet ${netuid} (${subnetNames[netuid]}).`;
    }

    const latestData = latestSnapshot.docs[0].data() as SubnetPool;
    
    const historicalQuery = query(
      collection(db, `subnet_pools/${netuid}/entries`),
      where('timestamp', '>=', new Date(Date.now() - hours * 60 * 60 * 1000).toISOString()),
      orderBy('timestamp', 'asc')
    );
    
    const historicalSnapshot = await getDocs(historicalQuery);
    const entries = historicalSnapshot.docs.map(doc => doc.data() as SubnetPool);
    
    // Convert string values to numbers and divide by 1e9 for TAO units
    const totalTao = Number(latestData.total_tao) / 1e9;
    const totalAlpha = Number(latestData.total_alpha) / 1e9;
    const alphaInPool = Number(latestData.alpha_in_pool) / 1e9;
    const alphaStaked = Number(latestData.alpha_staked) / 1e9;
    const marketCap = Number(latestData.market_cap) / 1e9;
    
    const totalTaoUsd = taoPrice ? totalTao * taoPrice : null;
    const totalAlphaUsd = taoPrice ? totalAlpha * taoPrice : null;
    const alphaInPoolUsd = taoPrice ? alphaInPool * taoPrice : null;
    const alphaStakedUsd = taoPrice ? alphaStaked * taoPrice : null;
    const marketCapUsd = taoPrice ? marketCap * taoPrice : null;

    // Collect all data for AI analysis
    let dataForAnalysis = `Market Analysis Report for Subnet ${netuid} (${subnetNames[netuid]})\n`;

    if (entries.length < 2) {
      dataForAnalysis += `\nMarket Cap: ${formatLargeNumber(marketCap)} τ${marketCapUsd ? ` ($${formatLargeNumber(marketCapUsd)})` : ''}
Total TAO in Pool: ${formatLargeNumber(totalTao)} τ${totalTaoUsd ? ` ($${formatLargeNumber(totalTaoUsd)})` : ''}
Alpha in Pool: ${formatLargeNumber(alphaInPool)} α${alphaInPoolUsd ? ` ($${formatLargeNumber(alphaInPoolUsd)})` : ''}
Circulating Supply: ${formatLargeNumber(totalAlpha)} α${totalAlphaUsd ? ` ($${formatLargeNumber(totalAlphaUsd)})` : ''}
Alpha Staked: ${formatLargeNumber(alphaStaked)} α${alphaStakedUsd ? ` ($${formatLargeNumber(alphaStakedUsd)})` : ''}`;

      // Add volume data if available
      if (volumeData) {
        const volume24h = volumeData.volume_24h / 1e9;
        const buyVolume = volumeData.buy_volume_24h / 1e9;
        const sellVolume = volumeData.sell_volume_24h / 1e9;
        
        const volumeUsd = taoPrice ? volume24h * taoPrice : null;
        const buyVolumeUsd = taoPrice ? buyVolume * taoPrice : null;
        const sellVolumeUsd = taoPrice ? sellVolume * taoPrice : null;
        
        dataForAnalysis += `\n\nTrading Activity (24h):
Volume: ${volume24h.toFixed(4)} τ${volumeUsd ? ` ($${volumeUsd.toFixed(2)})` : ''}
Buy Volume: ${buyVolume.toFixed(4)} τ${buyVolumeUsd ? ` ($${buyVolumeUsd.toFixed(2)})` : ''} (${volumeData.buys_24h} trades)
Sell Volume: ${sellVolume.toFixed(4)} τ${sellVolumeUsd ? ` ($${sellVolumeUsd.toFixed(2)})` : ''} (${volumeData.sells_24h} trades)`;
      }
    } else {
      const oldestEntry = entries[0];
      
      // Convert oldest entry values to TAO units for consistent comparison
      const oldAlphaStaked = Number(oldestEntry.alpha_staked) / 1e9;
      const oldMarketCap = Number(oldestEntry.market_cap) / 1e9;
      
      const alphaStakedChange = ((alphaStaked - oldAlphaStaked) / oldAlphaStaked) * 100;
      const marketCapChange = ((marketCap - oldMarketCap) / oldMarketCap) * 100;
      
      const timeAgo = formatDistanceToNow(new Date(oldestEntry.timestamp), { addSuffix: true });
      
      dataForAnalysis += `\nTime Period: ${timeAgo} to now

Market Cap: ${formatLargeNumber(marketCap)} τ${marketCapUsd ? ` ($${formatLargeNumber(marketCapUsd)})` : ''} (${marketCapChange >= 0 ? '+' : ''}${marketCapChange.toFixed(2)}% change)
Total TAO in Pool: ${formatLargeNumber(totalTao)} τ${totalTaoUsd ? ` ($${formatLargeNumber(totalTaoUsd)})` : ''}
Alpha in Pool: ${formatLargeNumber(alphaInPool)} α${alphaInPoolUsd ? ` ($${formatLargeNumber(alphaInPoolUsd)})` : ''}
Circulating Supply: ${formatLargeNumber(totalAlpha)} α${totalAlphaUsd ? ` ($${formatLargeNumber(totalAlphaUsd)})` : ''}
Alpha Staked: ${formatLargeNumber(alphaStaked)} α${alphaStakedUsd ? ` ($${formatLargeNumber(alphaStakedUsd)})` : ''} (${alphaStakedChange >= 0 ? '+' : ''}${alphaStakedChange.toFixed(2)}% change)`;

      // Add volume data if available
      if (volumeData) {
        const volume24h = volumeData.volume_24h / 1e9;
        const buyVolume = volumeData.buy_volume_24h / 1e9;
        const sellVolume = volumeData.sell_volume_24h / 1e9;
        
        const volumeUsd = taoPrice ? volume24h * taoPrice : null;
        const buyVolumeUsd = taoPrice ? buyVolume * taoPrice : null;
        const sellVolumeUsd = taoPrice ? sellVolume * taoPrice : null;
        
        dataForAnalysis += `\n\nTrading Activity (24h):
Volume: ${volume24h.toFixed(4)} τ${volumeUsd ? ` ($${volumeUsd.toFixed(2)})` : ''}
Buy Volume: ${buyVolume.toFixed(4)} τ${buyVolumeUsd ? ` ($${buyVolumeUsd.toFixed(2)})` : ''} (${volumeData.buys_24h} trades)
Sell Volume: ${sellVolume.toFixed(4)} τ${sellVolumeUsd ? ` ($${sellVolumeUsd.toFixed(2)})` : ''} (${volumeData.sells_24h} trades)`;
      }
    }

    // Add subnet description and context
    const subnetInfo = subnetDescriptions[netuid];
    if (subnetInfo?.description) {
      dataForAnalysis += '\n\nSubnet Overview:\n';
      dataForAnalysis += subnetInfo.description;
    }

    // Add tweet summary if available
    const tweetSummary = await generateTweetSummary(netuid);
    if (tweetSummary) {
      dataForAnalysis += tweetSummary;
    }

    // Get mindshare data
    const mindshareHistory = await getMindshareHistory(netuid);
    if (mindshareHistory && mindshareHistory.history.length >= 2) {
      const latest = mindshareHistory.history[0]; // Most recent entry is first in the array
      const oldest = mindshareHistory.history[mindshareHistory.history.length - 1];
      const currentMindshare = latest.mindsharePercentage;
      const mindshareChange = ((latest.mindsharePercentage - oldest.mindsharePercentage) / oldest.mindsharePercentage) * 100;
      
      dataForAnalysis += `\n\nMindshare Data:
Current Mindshare: ${currentMindshare.toFixed(2)}%
Change: ${mindshareChange >= 0 ? '+' : ''}${mindshareChange.toFixed(2)}%
Twitter Engagement Score: ${latest.twitterEngagement.toFixed(1)}/100
Market Cap Score: ${latest.marketCapScore.toFixed(1)}/100
Volume Score: ${latest.volumeScore.toFixed(1)}/100`;
    }

    // Generate analysis using OpenAI
    const analysis = await generateMarketAnalysis(dataForAnalysis);
    return analysis;
  } catch (error) {
    console.error('Error generating market cap report:', error);
    return 'Error: Failed to generate market cap analysis report.';
  }
}

async function getTopByMarketCap(limit: number = 5, type: 'gainers' | 'losers' = 'gainers'): Promise<string> {
  try {
    const subnets = Object.keys(subnetNames).map(id => parseInt(id));
    
    const allSubnetData = await Promise.all(
      subnets.map(async (netuid) => {
        try {
          const [poolSnapshot, volumeData] = await Promise.all([
            getDocs(query(
              collection(db, 'subnet_pools', netuid.toString(), 'entries'),
              orderBy('timestamp', 'desc'),
              firestoreLimit(1)
            )),
            getLatestVolumeData(netuid)
          ]);
          
          if (poolSnapshot.empty) return null;
          
          const entries = poolSnapshot.docs.map(doc => ({
            ...doc.data() as SubnetPool,
            timestamp: doc.data().timestamp
          }));
          
          const sortedEntries = entries.sort((a, b) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
          
          const latestEntry = sortedEntries[sortedEntries.length - 1];
          const priceChange24h = Number(latestEntry.price_change_1_day);
          
          if (isNaN(priceChange24h)) return null;
          
          return {
            netuid,
            name: subnetNames[netuid],
            marketCap: Number(latestEntry.market_cap) / 1e9, // Convert to TAO units
            price: Number(latestEntry.price),
            priceChange24h,
            volume24h: volumeData ? volumeData.volume_24h / 1e9 : Number(latestEntry.tao_volume_24_hr) / 1e9,
            timestamp: latestEntry.timestamp
          };
        } catch (error) {
          console.error(`Error processing subnet ${netuid}:`, error);
          return null;
        }
      })
    );
    
    const validResults = allSubnetData
      .filter((result): result is NonNullable<typeof allSubnetData[0]> => result !== null)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    
    const sortedResults = validResults
      .sort((a, b) => type === 'gainers' ? 
        b.priceChange24h - a.priceChange24h : 
        a.priceChange24h - b.priceChange24h
      )
      .slice(0, limit);
    
    if (sortedResults.length === 0) {
      return `No ${type} data available.`;
    }
    
    // Prepare data for AI analysis
    let dataForAnalysis = `Top ${limit} ${type === 'gainers' ? 'Gainers' : 'Losers'} in the last 24 hours:\n\n`;
    
    sortedResults.forEach((result, index) => {
      dataForAnalysis += `${index + 1}. ${result.name} (SN${result.netuid}): ${result.priceChange24h >= 0 ? '+' : ''}${result.priceChange24h.toFixed(2)}%\n`;
      dataForAnalysis += `   Price: ${result.price.toFixed(4)} τ\n`;
      dataForAnalysis += `   Market Cap: ${formatLargeNumber(result.marketCap)} τ\n`;
      dataForAnalysis += `   Volume (24h): ${formatLargeNumber(result.volume24h)} τ\n\n`;
    });
    
    // Generate analysis using OpenAI
    const analysis = await generateMarketAnalysis(dataForAnalysis);
    return analysis;
  } catch (error) {
    console.error('Error getting top market caps:', error);
    return 'Error: Failed to get top market caps.';
  }
}

async function getSignificantPriceMovements(hours: number = 24): Promise<string> {
  try {
    const subnets = Object.keys(subnetNames).map(id => parseInt(id));
    let totalTaoPrice = 0;
    let totalUsdValue = 0;
    const subnetPrices: Array<{netuid: number, name: string, price: number}> = [];
    
    await rateLimiter.checkRateLimit();
    const taoResponse = await axios.get(
      'https://api.taostats.io/api/price/latest/v1?asset=tao',
      {
        headers: {
          'accept': 'application/json',
          'Authorization': 'tao-1b237a3c-dcb4-468c-ad70-6b47e40e2552:6473c9a3'
        }
      }
    );
    
    const taoPrice = taoResponse.data?.data?.[0]?.price 
      ? Number(taoResponse.data.data[0].price) 
      : null;

    for (const netuid of subnets) {
      try {
        const entriesRef = collection(db, `subnet_pools/${netuid}/entries`);
        const entriesQuery = query(
          entriesRef,
          orderBy('timestamp', 'desc'),
          firestoreLimit(1)
        );
        
        const entriesSnapshot = await getDocs(entriesQuery);
        
        if (!entriesSnapshot.empty) {
          const latestData = entriesSnapshot.docs[0].data();
          const price = Number(latestData.price);
          
          if (!isNaN(price) && price > 0) {
            totalTaoPrice += price;
            if (taoPrice) {
              totalUsdValue += price * taoPrice;
            }
            
            subnetPrices.push({
              netuid,
              name: subnetNames[netuid],
              price
            });
          }
        }
      } catch (error) {
        console.error(`Error processing subnet ${netuid}:`, error);
        continue;
      }
    }
    
    subnetPrices.sort((a, b) => b.price - a.price);
    
    // Prepare data for AI analysis
    let dataForAnalysis = 'Subnet Price Summary\n\n';
    
    dataForAnalysis += `Total TAO Value: ${totalTaoPrice.toFixed(4)} τ\n`;
    if (taoPrice) {
      dataForAnalysis += `Total USD Value: $${totalUsdValue.toFixed(2)}\n`;
    }
    dataForAnalysis += '\nTop 10 Subnets by Price:\n\n';
    
    subnetPrices.slice(0, 10).forEach(subnet => {
      dataForAnalysis += `${subnet.name} (SN${subnet.netuid}): ${subnet.price.toFixed(4)} τ`;
      if (taoPrice) {
        dataForAnalysis += ` ($${(subnet.price * taoPrice).toFixed(2)})`;
      }
      dataForAnalysis += '\n';
    });
    
    // Generate analysis using OpenAI
    const analysis = await generateMarketAnalysis(dataForAnalysis);
    return analysis;
  } catch (error) {
    console.error('Error calculating total subnet price:', error);
    return 'Error: Failed to calculate total subnet price.';
  }
}

async function getTotalSubnetPrice(): Promise<string> {
  try {
    const subnets = Object.keys(subnetNames).map(id => parseInt(id));
    let totalTaoPrice = 0;
    let totalUsdValue = 0;
    const subnetPrices: Array<{netuid: number, name: string, price: number}> = [];
    
    await rateLimiter.checkRateLimit();
    const taoResponse = await axios.get(
      'https://api.taostats.io/api/price/latest/v1?asset=tao',
      {
        headers: {
          'accept': 'application/json',
          'Authorization': 'tao-1b237a3c-dcb4-468c-ad70-6b47e40e2552:6473c9a3'
        }
      }
    );
    
    const taoPrice = taoResponse.data?.data?.[0]?.price 
      ? Number(taoResponse.data.data[0].price) 
      : null;

    for (const netuid of subnets) {
      try {
        const entriesRef = collection(db, `subnet_pools/${netuid}/entries`);
        const entriesQuery = query(
          entriesRef,
          orderBy('timestamp', 'desc'),
          firestoreLimit(1)
        );
        
        const entriesSnapshot = await getDocs(entriesQuery);
        
        if (!entriesSnapshot.empty) {
          const latestData = entriesSnapshot.docs[0].data();
          const price = Number(latestData.price);
          
          if (!isNaN(price) && price > 0) {
            totalTaoPrice += price;
            if (taoPrice) {
              totalUsdValue += price * taoPrice;
            }
            
            subnetPrices.push({
              netuid,
              name: subnetNames[netuid],
              price
            });
          }
        }
      } catch (error) {
        console.error(`Error processing subnet ${netuid}:`, error);
        continue;
      }
    }
    
    subnetPrices.sort((a, b) => b.price - a.price);
    
    // Direct report without AI analysis
    let report = 'Subnet Price Summary\n\n';
    
    report += `Total TAO Value: ${totalTaoPrice.toFixed(4)} τ\n`;
    if (taoPrice) {
      report += `Total USD Value: $${totalUsdValue.toFixed(2)}\n`;
    }
    report += '\nTop 10 Subnets by Price:\n\n';
    
    subnetPrices.slice(0, 10).forEach(subnet => {
      report += `${subnet.name} (SN${subnet.netuid}): ${subnet.price.toFixed(4)} τ`;
      if (taoPrice) {
        report += ` ($${(subnet.price * taoPrice).toFixed(2)})`;
      }
      report += '\n';
    });
    
    return report;
  } catch (error) {
    console.error('Error calculating total subnet price:', error);
    return 'Error: Failed to calculate total subnet price.';
  }
}

async function getInvestmentAnalysis(netuid: number): Promise<string> {
  try {
    await rateLimiter.checkRateLimit();
    const taoResponse = await axios.get(
      'https://api.taostats.io/api/price/latest/v1?asset=tao',
      {
        headers: {
          'accept': 'application/json',
          'Authorization': 'tao-1b237a3c-dcb4-468c-ad70-6b47e40e2552:6473c9a3'
        }
      }
    );
    
    const taoPrice = taoResponse.data?.data?.[0]?.price 
      ? Number(taoResponse.data.data[0].price) 
      : null;

    const [entriesSnapshot, volumeData] = await Promise.all([
      getDocs(query(
        collection(db, `subnet_pools/${netuid}/entries`),
        orderBy('timestamp', 'desc'),
        firestoreLimit(7)
      )),
      getLatestVolumeData(netuid)
    ]);
    
    if (entriesSnapshot.empty) {
      return `No market data available for ${subnetNames[netuid]} (SN${netuid}).`;
    }

    const entries = entriesSnapshot.docs.map(doc => doc.data() as SubnetPool);
    const latestEntry = entries[0];
    const sortedEntries = entries.sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    // Get the most up-to-date mindshare history
    const mindshareHistory = await getMindshareHistory(netuid);
    const tweetSummary = await generateTweetSummary(netuid);

    const price = Number(latestEntry.price);
    const usdPrice = taoPrice ? price * taoPrice : null;
    const priceChange24h = Number(latestEntry.price_change_1_day);
    const weekAgoPrice = Number(sortedEntries[0]?.price || 0);
    const priceChange7d = weekAgoPrice > 0 
      ? ((price - weekAgoPrice) / weekAgoPrice) * 100 
      : 0;
    
    // Use volume data from Firestore if available, otherwise fallback to pool data
    const volume24h = volumeData ? volumeData.volume_24h / 1e9 : Number(latestEntry.tao_volume_24_hr) / 1e9;
    const volumeUsd = taoPrice ? volume24h * taoPrice : null;
    const buys24h = volumeData?.buys_24h || Number(latestEntry.buys_24_hr);
    const sells24h = volumeData?.sells_24h || Number(latestEntry.sells_24_hr);
    const marketCap = Number(latestEntry.market_cap) / 1e9; // Convert to TAO units

    let mindshareChange = 0;
    let currentMindshare = 0;
    if (mindshareHistory?.history.length >= 2) {
      const latest = mindshareHistory.history[0]; // Most recent entry is first in the array
      const oldest = mindshareHistory.history[mindshareHistory.history.length - 1];
      currentMindshare = latest.mindsharePercentage;
      mindshareChange = ((latest.mindsharePercentage - oldest.mindsharePercentage) / oldest.mindsharePercentage) * 100;
    }

    // Collect all data for AI analysis
    let dataForAnalysis = `Investment Analysis for ${subnetNames[netuid]} (SN${netuid})\n\n`;

    // Price and market data
    dataForAnalysis += `Price: ${price.toFixed(4)} τ`;
    if (usdPrice) dataForAnalysis += ` ($${usdPrice.toFixed(4)})`;
    dataForAnalysis += `\n24h Change: ${priceChange24h >= 0 ? '+' : ''}${priceChange24h.toFixed(2)}%`;
    dataForAnalysis += `\n7d Change: ${priceChange7d >= 0 ? '+' : ''}${priceChange7d.toFixed(2)}%`;
    dataForAnalysis += `\nVolume (24h): ${volume24h.toFixed(4)} τ${volumeUsd ? ` ($${volumeUsd.toFixed(2)})` : ''}`;
    dataForAnalysis += `\nMarket Cap: ${formatLargeNumber(marketCap)} τ`;
    
    // Trading activity
    if (volumeData) {
      const buyVolume = volumeData.buy_volume_24h / 1e9;
      const sellVolume = volumeData.sell_volume_24h / 1e9;
      const buyVolumeUsd = taoPrice ? buyVolume * taoPrice : null;
      const sellVolumeUsd = taoPrice ? sellVolume * taoPrice : null;
      
      dataForAnalysis += `\nBuy Volume: ${buyVolume.toFixed(4)} τ${buyVolumeUsd ? ` ($${buyVolumeUsd.toFixed(2)})` : ''} (${volumeData.buys_24h} trades)`;
      dataForAnalysis += `\nSell Volume: ${sellVolume.toFixed(4)} τ${sellVolumeUsd ? ` ($${sellVolumeUsd.toFixed(2)})` : ''} (${volumeData.sells_24h} trades)`;
      
      // Calculate buy/sell ratio
      const buyRatio = buys24h / (buys24h + sells24h);
      dataForAnalysis += `\nBuy Pressure: ${(buyRatio * 100).toFixed(1)}% of trades are buys`;
    }
    
    // Mindshare data
    if (mindshareHistory) {
      dataForAnalysis += `\n\nMindshare: ${currentMindshare.toFixed(2)}% (${mindshareChange >= 0 ? '+' : ''}${mindshareChange.toFixed(2)}%)`;
      
      // Add more detailed mindshare metrics if available
      if (mindshareHistory.history.length > 0) {
        const latest = mindshareHistory.history[0];
        dataForAnalysis += `\nTwitter Engagement: ${latest.twitterEngagement.toFixed(1)}/100`;
        dataForAnalysis += `\nMarket Cap Score: ${latest.marketCapScore.toFixed(1)}/100`;
        dataForAnalysis += `\nRank Score: ${latest.rankScore.toFixed(1)}/100`;
        dataForAnalysis += `\nVolume Score: ${latest.volumeScore.toFixed(1)}/100`;
      }
    }

    // Add subnet description and context
    const subnetInfo = subnetDescriptions[netuid];
    if (subnetInfo?.description) {
      dataForAnalysis += '\n\nSubnet Overview:\n';
      dataForAnalysis += subnetInfo.description;
    }

    // Add tweet summary if available
    if (tweetSummary) {
      dataForAnalysis += tweetSummary;
    }

    // Generate analysis using OpenAI
    const analysis = await generateMarketAnalysis(dataForAnalysis);
    return analysis;
  } catch (error) {
    console.error('Error generating investment analysis:', error);
    return 'Error: Failed to generate investment analysis.';
  }
}

function formatLargeNumber(value: string | number): string {
  let num: number;
  if (typeof value === 'string') {
    num = parseFloat(value);
  } else {
    num = value;
  }

  const divisions = [
    { value: 1e3, symbol: 'K' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'B' },
    { value: 1e12, symbol: 'T' }
  ];

  for (let i = divisions.length - 1; i >= 0; i--) {
    const division = divisions[i];
    if (num >= division.value) {
      return (num / division.value).toFixed(2) + division.symbol;
    }
  }

  return num.toFixed(4);
}

// Export functions
export {
  getPriceReport,
  getMarketCapReport,
  getTopByMarketCap,
  getSignificantPriceMovements,
  getTotalSubnetPrice,
  getInvestmentAnalysis,
  analyzeSubnetPotential
};